/**
 * @file ConsentDialog.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import '../css/Consent.css'
import { Box} from '@mui/system';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Stack } from '@mui/material';
import { ConsentTranslation } from '../../../services/models/ConsentTranslation';
import { ConsentStatus } from '../../login/utils/ConsentStatus';
import { ApiError } from '../../../services/models/ApiError';
import { ConsentType } from '../../login/utils/ConsentType';
import { AppLanguageList, defaultLanguage } from '../../utils/AppConstant';
import { AppProgressBar } from '../../utils/AppProgressBar';
import { BaseConsentComponent } from './base/BaseConsentComponent';

/**
 * A type of Component state
 */
type CompState = {
    loading: boolean; currentTranslation?: ConsentTranslation; currentConsentId?: string; consentStatus?: ConsentStatus
}

/**
 * An interface definition for Component Props
 */
interface CompProps {
    hidePrivacyNotice : any;
    consentType: ConsentType;
}

/**
 * A class definition for ConsentDialog
 */
export default class ConsentDialog extends BaseConsentComponent<CompProps, CompState> {
    
    constructor(props: CompProps) {
        super(props);
        this.state = {
            loading : false
        }
    }

    /**
     * A class component lifeCycle's method
     */
    componentDidMount(): void {
        this.subscribeApiEvents();
        this.fetchConsents();
    }

    /**
     * A class component lifeCycle's method
     */    
    componentWillUnmount(): void {
        this.unSubscribeApiEvents();
    }

    /**
     * Subscribes Api Events
     */
    private subscribeApiEvents = ()=>{
        this.consentViewModel.onConsentLoaded.subscribe((response)=>{
            this.consent = response;
            const translation = this.getLanguageConsentContent(defaultLanguage.code, ConsentDialog.name);
            this.setConsentContent(translation!!); // eslint-disable-line
            this.toggleLoading(false);
            this.logger.verbose(`onConsentLoaded Size : ${JSON.stringify(response.length)}`, ConsentDialog.name);
        });

        this.consentViewModel.onConsentLoadedError.subscribe((apiError: ApiError)=>{
            this.toggleLoading(false);
            this.logger.verbose(`onConsentLoadedError : ${JSON.stringify(apiError)}`, ConsentDialog.name);
        });
    }

    /**
     * un Subscribes Api Events
     */
    private unSubscribeApiEvents = ()=> {
        this.consentViewModel.onConsentLoaded.unsubscribe();
        this.consentViewModel.onConsentLoadedError.unsubscribe();
    }

    /**
     * Fetches consents
     */
    private fetchConsents = ()=> {
        this.toggleLoading(true);
        const accessToken = this.validateAndGetSession(ConsentDialog.name);
        const consentType = encodeURIComponent(this.props.consentType === ConsentType.Privacy ? 'Privacy' : 'T&Cs');
        const queryType = { type: consentType }
        this.consentViewModel.getConsents(accessToken, queryType);
    }

    /**
     * Toggles loading
     * @param showLoading boolean
     */
    private toggleLoading = (showLoading: boolean)=> {
            this.setState({
                loading: showLoading
            });
        } 

    /**
     * Sets consent's content
     * @param content ConsentTranslation
     */
    private setConsentContent = (content: ConsentTranslation)=> {
        this.setState({
            currentTranslation: content, currentConsentId: this.consent[0].versions[0].id
        });
    }        

    /**
     * Handles language change
     * @param event 
     */
    private handleChange = (event: SelectChangeEvent) => {
        const translation = this.getLanguageConsentContent((event.target.value as string), ConsentDialog.name);
        this.setConsentContent(translation!!); // eslint-disable-line
        };

        render()  {
           return <React.Fragment>
                    <AppBar className='ConsetDialogHeader' position="relative" style={{ width: "100%" }} elevation={0}>
                        <Toolbar disableGutters variant="dense" className="HomeToolBar" style={{ width: "100%", }}>
                        <img src="/../log_header.svg" className="HeaderBridgeLogo" alt=""/>
                            <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" }, alignItems: "center" }} >
                            </Box>
                            <img alt="More" src="/../closeIcon_consent.svg" className="ConsentCloseBtn" onClick={()=>{
                                this.props.hidePrivacyNotice();
                            }}/>

                        </Toolbar>
                    </AppBar>
                    <FormControl className='ConsentLanguage'>
                    <InputLabel id="demo-simple-select-label">Select a language</InputLabel>
                                    <Select
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        value={ !this.state.currentTranslation ? 'en' : this.state.currentTranslation?.language} sx={{borderRadius: "15px"}}
                                        label="Select a language"
                                        onChange={this.handleChange}>
                                            { AppLanguageList.map((item: any, index: number)=> 
                                            <MenuItem value={item.code} key={index}>{item.name}</MenuItem> )}
                                    </Select>
                    </FormControl>
                    { !this.state.loading && <Stack
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                        spacing={2}>                                 
                            <p className='ConsentContent'>
                            <h1>{this.state.currentTranslation?.title as string}</h1>
                            </p>
                            <div dangerouslySetInnerHTML={{__html: this.state.currentTranslation?.content.content as string}} className='ConsentContent' />
                    </Stack>
                }
                {
                        this.state.loading && <div className="ConsentLoadingBox"> <AppProgressBar color='primary'/> </div>
                }
                </React.Fragment>
        }            

}